import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '../AppShell/AppShell';
import { AuthGuard } from '../AuthGuard';
import { renderOutOfShellRoutes } from './out-of-shell-routes';
import { RedirectPreservingSearch } from './RedirectPreservingSearch';
import { inShellRouteEntries, topLevelRouteEntries } from './route-table';

import type { PageOrRedirectRouteEntry } from './route-table';
import type { ReactElement } from 'react';

/**
 * Top-level route tree. The route inventory itself is the typed table in
 * `./route-table` (the single source of truth); this file only maps that table
 * to `<Route>` elements, so the router and the table can never drift — a
 * `route-table.test.tsx` drift guard asserts the rendered route set equals the
 * table exactly. Page chunks are declared in `./lazy-pages` (A23 lazy chunking).
 *
 *  / ───────────── front door: redirect to /home (auth) or /signin (anon)
 *  /signin ─────── Auth0 hosted-login launcher
 *  ─ AuthGuard + AppShell layout route (every in-shell entry)
 *  /app ────────── redirect to /home
 *  * ───────────── 404
 *
 * Authenticated (in-shell) routes are nested under a single AuthGuard + AppShell
 * layout route so the chrome (sidebar / top bar) mounts once and the route outlet
 * swaps the page content. Suspense placement: the in-shell boundary lives around
 * AppShell's Outlet (so the chrome stays painted during a chunk load — see
 * AppShell.tsx); the boundary in `out-of-shell-routes` covers the OUTSIDE-shell
 * lazy routes.
 */
export function AppRoutes(): ReactElement {
  return (
    <Routes>
      {topLevelRouteEntries.map(renderPageOrRedirect)}
      {/* Public + out-of-shell interstitial routes (extracted for the file-size
          ceiling) — React Router flattens this fragment's `<Route>` children. */}
      {renderOutOfShellRoutes()}
      <Route
        element={
          <AuthGuard>
            <AppShell />
          </AuthGuard>
        }
      >
        {inShellRouteEntries.map(renderPageOrRedirect)}
      </Route>
    </Routes>
  );
}

/**
 * Maps a page-or-redirect table entry to its `<Route>`. A `page` renders its
 * component; a `redirect` renders a `<Navigate replace>` — or `RedirectPreservingSearch`
 * when the entry carries the query string across (e.g. `/user-learning` → `/users`).
 */
function renderPageOrRedirect(entry: PageOrRedirectRouteEntry): ReactElement {
  if (entry.kind === 'redirect') {
    return (
      <Route
        key={entry.path}
        path={entry.path}
        element={
          entry.preserveSearch ? (
            <RedirectPreservingSearch to={entry.to} />
          ) : (
            <Navigate to={entry.to} replace />
          )
        }
      />
    );
  }
  const Component = entry.component;
  if (entry.children === undefined) {
    return <Route key={entry.path} path={entry.path} element={<Component />} />;
  }
  // A LAYOUT entry: `component` renders an `<Outlet>` and stays mounted while the
  // children swap, which is the whole point of nesting it (the parent's shared
  // read happens once, not on every sub-navigation). A child with no `path` is
  // the index route.
  return (
    <Route key={entry.path} path={entry.path} element={<Component />}>
      {entry.children.map((child) => {
        const ChildComponent = child.component;
        return child.path === undefined ? (
          <Route key="index" index element={<ChildComponent />} />
        ) : (
          <Route key={child.path} path={child.path} element={<ChildComponent />} />
        );
      })}
    </Route>
  );
}
